/**
 * Shared Mapper Utilities
 *
 * Pure helper functions used by entity mappers (customer, vehicle, etc.)
 * to produce clean Firestore write models.
 *
 * No side effects, no service/route imports.
 */

/**
 * Extract only the fields marked dirty by React Hook Form.
 *
 * RHF's `dirtyFields` is a nested object where `true` means the leaf was
 * modified and nested objects contain the same structure recursively.
 *
 * This function walks the `dirtyFields` tree and picks only the
 * corresponding values from `allValues`, preserving nesting.
 *
 * Optional string fields that are empty (`""`) are normalized to `null`
 * so that "clearing a field" persists as `null` in Firestore rather than
 * an empty string.
 *
 * @example
 *   pickDirtyFields(
 *     { lastName: true, address: { city: true } },
 *     { firstName: 'A', lastName: 'B', address: { street: 'X', city: '' } }
 *   )
 *   // → { lastName: 'B', address: { city: null } }
 */
export function pickDirtyFields<T extends Record<string, unknown>>(
  dirtyFields: Record<string, unknown>,
  allValues: T
): Partial<T> {
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(dirtyFields)) {
    const dirtyVal = dirtyFields[key];
    const value = (allValues as Record<string, unknown>)[key];

    if (dirtyVal === true) {
      // Leaf field — normalize empty strings to null for optional fields
      result[key] = typeof value === 'string' && value.trim() === '' ? null : value;
    } else if (dirtyVal && typeof dirtyVal === 'object' && !Array.isArray(dirtyVal)) {
      // Nested object — recurse
      result[key] = pickDirtyFields(
        dirtyVal as Record<string, unknown>,
        (value ?? {}) as Record<string, unknown>
      );
    }
    // Arrays: if dirtyVal is an array, include the whole value
    else if (Array.isArray(dirtyVal)) {
      result[key] = value;
    }
  }

  return result as Partial<T>;
}

/**
 * Normalize a value for change-detection comparison.
 *
 * Treats `null`, `undefined`, and `""` (empty / whitespace-only string)
 * as equivalent so that audit diff builders don't record spurious
 * `null → ""` transitions.
 */
export function normalizeForComparison(v: unknown): unknown {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  return v;
}

/**
 * Remove keys whose value is null, undefined, or empty string (after trim).
 * Preserves numbers (including 0), booleans, and non-empty strings.
 */
export function omitNullish<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed === '') continue;
      out[k] = trimmed;
    } else {
      out[k] = v;
    }
  }
  return out as Partial<T>;
}
